import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Code, Users, Building } from "lucide-react";
import { FundAllocationChart } from "@/components/use-of-funds/FundAllocationChart";
import { FundBreakdown } from "@/components/use-of-funds/FundBreakdown";
import { KeyConsiderations } from "@/components/use-of-funds/KeyConsiderations";
import { InvestmentOpportunity } from "@/components/use-of-funds/InvestmentOpportunity";
import { useFundAllocation } from "@/hooks/useFundAllocation";
import { LoadingState } from "@/components/ui/loading-state";

const UseOfFunds = () => {
  const { data: fundAllocationData, isLoading, error } = useFundAllocation();

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !fundAllocationData) {
    return <div>Error loading fund allocation data</div>;
  }

  const fundAllocation = fundAllocationData.map(item => ({
    category: item.category,
    amount: item.amount,
    percentage: item.percentage,
    icon: getIconForCategory(item.category),
    description: item.description,
    breakdown: (item.breakdown_items || []).map(breakdownItem => ({
      item: breakdownItem.item,
      allocation: breakdownItem.allocation
    })),
    color: item.color
  }));

  const chartData = fundAllocationData.map(item => ({
    name: item.category,
    amount: item.amount,
    color: item.color
  }));

  const totalFunding = fundAllocationData.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <InvestmentOpportunity />
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-primary">Use of Funds - ${totalFunding.toLocaleString()}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-gray-600 mb-6">
              Our funding allocation strategy focuses on key areas to ensure sustainable growth and development of our platform.
            </p>
          </div>

          <FundAllocationChart chartData={chartData} />
          <FundBreakdown fundAllocation={fundAllocation} />
          <KeyConsiderations />
        </CardContent>
      </Card>
    </div>
  );
};

const getIconForCategory = (category: string) => {
  switch (category) {
    case "Product Development":
      return Code;
    case "Business Development":
      return Users;
    case "G&A":
      return Building;
    default:
      return Code;
  }
};

export default UseOfFunds;